import {
  DocumentNode,
  StoreObject,
  gql,
  useApolloClient,
} from "@apollo/client";
import {
  DataBrowserSemanticIdType,
  SpecialVersionType,
  semanticDatasetTitleString,
} from "@hex/common";
import { isEmpty } from "lodash";
import React from "react";
import styled from "styled-components";

import {
  SemanticDataset,
  SemanticProject,
  SemanticView,
  Status,
} from "../../../generated/graphqlTypes";
import { Flex, HexAnchorButton } from "../../../hex-components";
import { useTerminology } from "../../../hooks/useTerminology";
import { ORG_ID } from "../../../orgs";
import { Routes } from "../../../route/routes";
import { StatusLabel } from "../../common/labels/StatusLabel";
import { Heading } from "../../Heading";
import { useCategoriesAndStatusesForOrg } from "../../home/project-filters/hooks/useCategoriesAndStatusesForProjectFilters";
import { SemanticDatasetIcon, SemanticViewIcon } from "../../icons/CustomIcons";

import { RichTextMentionData } from "./RichTextMentionPlugin";
import {
  SemanticDatasetMentionDetailsFragmentDoc,
  SemanticViewMentionDetailsFragmentDoc,
  useSemanticDatasetMentionDetailsQuery,
  useSemanticViewMentionDetailsQuery,
} from "./SemanticResourceMentionDetails.generated";

gql`
  fragment SemanticDatasetMentionDetailsFragment on SemanticDataset {
    id
    name
    description
    status {
      id
    }
    semanticProject {
      id
      name
    }
  }

  fragment SemanticViewMentionDetailsFragment on SemanticView {
    id
    name
    description
    status {
      id
    }
    semanticProject {
      id
      name
    }
  }

  query SemanticDatasetMentionDetailsQuery(
    $semanticDatasetName: SemanticDatasetName!
    $semanticProjectId: SemanticProjectId!
    $semanticProjectVersionString: SemanticProjectVersionString!
  ) {
    semanticDatasetByName(
      semanticDatasetName: $semanticDatasetName
      semanticProjectId: $semanticProjectId
      semanticProjectVersionString: $semanticProjectVersionString
    ) {
      ...SemanticDatasetMentionDetailsFragment
    }
  }

  query SemanticViewMentionDetailsQuery(
    $semanticViewName: SemanticViewName!
    $semanticProjectId: SemanticProjectId!
    $semanticProjectVersionString: SemanticProjectVersionString!
  ) {
    semanticViewByName(
      semanticViewName: $semanticViewName
      semanticProjectId: $semanticProjectId
      semanticProjectVersionString: $semanticProjectVersionString
    ) {
      ...SemanticViewMentionDetailsFragment
    }
  }
`;

export const SemanticDatasetMentionDetails = React.memo(
  function SemanticDatasetMentionDetails({
    mention,
  }: {
    mention: RichTextMentionData.SemanticDataset;
  }) {
    const semanticDataset = useReadFragmentFallbackToQuery({
      fragmentDoc: SemanticDatasetMentionDetailsFragmentDoc,
      cacheIdentity: { __typename: "SemanticDataset", id: mention.id },
      useQuery: (run) =>
        useSemanticDatasetMentionDetailsQuery({
          variables: {
            semanticDatasetName: mention.name,
            semanticProjectId: mention.semanticProjectId,
            semanticProjectVersionString: SpecialVersionType.LAST_PUBLISHED,
          },
          skip: !run,
        }).data?.semanticDatasetByName,
    });
    return (
      <SemanticResourceMentionDetails
        mention={mention}
        semanticResource={semanticDataset}
      />
    );
  },
);

export const SemanticViewMentionDetails = React.memo(
  function SemanticViewMentionDetails({
    mention,
  }: {
    mention: RichTextMentionData.SemanticView;
  }) {
    const semanticView = useReadFragmentFallbackToQuery({
      fragmentDoc: SemanticViewMentionDetailsFragmentDoc,
      cacheIdentity: { __typename: "SemanticView", id: mention.id },
      useQuery: (run) =>
        useSemanticViewMentionDetailsQuery({
          variables: {
            semanticViewName: mention.name,
            semanticProjectId: mention.semanticProjectId,
            semanticProjectVersionString: SpecialVersionType.LAST_PUBLISHED,
          },
          skip: !run,
        }).data?.semanticViewByName,
    });
    return (
      <SemanticResourceMentionDetails
        mention={mention}
        semanticResource={semanticView}
      />
    );
  },
);

/**
 * These items are likely in the Apollo cache as a fragment populated from the
 * search query triggered by the `@` mention, but since that's a _search_, Apollo
 * wouldn't know to populate it into the cache for `query { semantic___ByName } `.
 * So this function attempts to find it in the cache, and falls back to the query
 * otherwise.
 */
function useReadFragmentFallbackToQuery<T>({
  cacheIdentity,
  fragmentDoc,
  useQuery,
}: {
  fragmentDoc: DocumentNode;
  cacheIdentity: StoreObject;
  useQuery: (run: boolean) => T | undefined;
}): T | undefined {
  const apolloClient = useApolloClient();
  const fragmentResult = apolloClient.cache.readFragment<T>({
    fragment: fragmentDoc,
    id: apolloClient.cache.identify(cacheIdentity),
    returnPartialData: false,
  });
  const hasFragmentResult = !isEmpty(fragmentResult);
  const queryResult = useQuery(!hasFragmentResult);
  return hasFragmentResult ? (fragmentResult ?? undefined) : queryResult;
}

const SemanticResourceMentionDetails = React.memo(
  function SemanticResourceMentionDetails({
    mention,
    semanticResource,
  }: {
    mention:
      | RichTextMentionData.SemanticDataset
      | RichTextMentionData.SemanticView;
    semanticResource:
      | (Pick<SemanticDataset | SemanticView, "name" | "description"> & {
          status: Pick<Status, "id"> | null;
          semanticProject: Pick<SemanticProject, "name">;
        })
      | undefined;
  }) {
    const { dataBrowserText } = useTerminology();
    const { statusMap } = useCategoriesAndStatusesForOrg();

    const semanticProject = semanticResource?.semanticProject;
    const appliedStatus = semanticResource?.status
      ? (statusMap.get(semanticResource.status.id) ?? null)
      : null;

    const dataBrowserHref = Routes.DATA_BROWSER.href(ORG_ID, true, {
      tab: "semantic-layer",
      dataSourceId: mention.semanticProjectId,
      detailViewType:
        mention.mentionType === "semantic_dataset"
          ? DataBrowserSemanticIdType.DATASET
          : DataBrowserSemanticIdType.VIEW,
      semanticResourceName: mention.name,
    });

    if (!semanticResource) {
      return null;
    }

    return (
      <Flex
        align="start"
        css="padding: 5px 2px; width: 100%"
        direction="column"
        gap={4}
        justify="start"
      >
        {semanticProject?.name && (
          <SemanticProjectName>{semanticProject.name}</SemanticProjectName>
        )}

        <TitleRow renderAs="h2">
          {mention.mentionType === "semantic_dataset" ? (
            <SemanticDatasetIcon />
          ) : (
            <SemanticViewIcon />
          )}
          <SemanticModelName>
            {semanticDatasetTitleString(semanticResource)}
          </SemanticModelName>
        </TitleRow>

        {appliedStatus && (
          <Flex justify="start">
            <StatusLabel status={appliedStatus} />
          </Flex>
        )}

        {semanticResource.description && (
          <Description>{semanticResource.description}</Description>
        )}

        <StyledHexAnchorButton
          href={dataBrowserHref}
          small={true}
          target="_blank"
        >
          Show in {dataBrowserText}
        </StyledHexAnchorButton>
      </Flex>
    );
  },
);

const SemanticProjectName = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  color: ${({ theme }) => theme.fontColor.MUTED};
`;

const TitleRow = styled(Heading)`
  display: flex;
  align-items: flex-start;
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  line-height: 14px;
  max-width: 100%;
  gap: 4px;
`;

const SemanticModelName = styled.span`
  transform: none;
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  white-space: normal;
  overflow: hidden;
  overflow-wrap: break-word;
  display: inline-block;
  max-width: 220px;
  min-width: 0;
`;

const Description = styled.div`
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  color: ${({ theme }) => theme.fontColor.MUTED};
  line-height: ${({ theme }) => theme.lineHeight.SMALL};

  max-height: 50px;
  overflow: hidden;
`;

const StyledHexAnchorButton = styled(HexAnchorButton)`
  align-self: stretch;
  text-decoration: none !important;
`;
